import { useCallback, useEffect, useRef, useState } from 'react';

const BLOCK = 'LStudio__app_split';

/*
    🔥 PANELES REDIMENSIONABLES

    Explorador + detalle, editor + vista previa, lista + mapa.

    ⚠️ USA POINTER EVENTS, NO MOUSE EVENTS

    pointerdown/move/up cubren mouse, touch y lápiz con un solo juego
    de handlers. Con mouse events, en una tablet la barra no se puede
    arrastrar.

    ⚠️ setPointerCapture ES LO QUE EVITA EL BUG CLÁSICO

    Sin captura, si arrastrás rápido el puntero se sale del divisor y
    los eventos se van a otro elemento: la barra se queda pegada
    siguiendo al mouse aunque hayas soltado. Con captura, el divisor
    recibe todos los eventos hasta que soltás.

    ⚠️ TAMBIÉN SE MUEVE CON EL TECLADO

    Un divisor que solo responde al arrastre es inaccesible. Con foco,
    las flechas lo mueven de a 2% y Home/End van a los extremos.
*/

export const SplitPane = ({
    children,
    orientation = 'horizontal',   // horizontal (lado a lado) | vertical (arriba/abajo)
    initial = 50,                 // % del primer panel
    min = 15,
    max = 85,
    isCollapsible = false,        // doble clic en el divisor colapsa el primero
    storageKey = null,            // recuerda la posición
    onChange = null,
}) => {

    const REF__useRefRoot = useRef(null);

    const [usePercent, setPercent] = useState(() => {

        if (!storageKey) return initial;

        try {
            const SAVED = window.localStorage.getItem(`LStudio__split_${storageKey}`);
            return SAVED ? Number(SAVED) : initial;
        } catch {
            return initial;
        }
    });

    const [useIsDragging, setIsDragging] = useState(false);
    const [useCollapsed, setCollapsed] = useState(false);

    const IS_VERTICAL = orientation === 'vertical';

    const CB__onCommit = useCallback((value) => {

        const CLAMPED = Math.min(max, Math.max(min, value));

        setPercent(CLAMPED);
        onChange?.(CLAMPED);

        if (storageKey) {
            try { window.localStorage.setItem(`LStudio__split_${storageKey}`, String(CLAMPED)); } catch { /* nada */ }
        }

    }, [min, max, onChange, storageKey]);

    const onPointerDown = (event) => {
        event.currentTarget.setPointerCapture(event.pointerId);
        setIsDragging(true);
    };

    const onPointerMove = (event) => {

        if (!useIsDragging || !REF__useRefRoot.current) return;

        const BOX = REF__useRefRoot.current.getBoundingClientRect();

        const VALUE = IS_VERTICAL
            ? ((event.clientY - BOX.top) / BOX.height) * 100
            : ((event.clientX - BOX.left) / BOX.width) * 100;

        CB__onCommit(VALUE);
    };

    const onPointerUp = (event) => {
        event.currentTarget.releasePointerCapture?.(event.pointerId);
        setIsDragging(false);
    };

    const onKeyDown = (event) => {

        const STEP = event.shiftKey ? 10 : 2;

        const BACK = IS_VERTICAL ? 'ArrowUp' : 'ArrowLeft';
        const FWD = IS_VERTICAL ? 'ArrowDown' : 'ArrowRight';

        if (event.key === BACK) { event.preventDefault(); CB__onCommit(usePercent - STEP); }
        if (event.key === FWD) { event.preventDefault(); CB__onCommit(usePercent + STEP); }
        if (event.key === 'Home') { event.preventDefault(); CB__onCommit(min); }
        if (event.key === 'End') { event.preventDefault(); CB__onCommit(max); }
    };

    /*
        ⚠️ MIENTRAS ARRASTRÁS, NADA SE SELECCIONA

        Sin esto el arrastre selecciona el texto de los dos paneles y
        queda todo azul. Va en el <body> porque el puntero puede estar
        sobre cualquiera de los dos.
    */
    useEffect(() => {

        if (!useIsDragging) return;

        const PREV = document.body.style.userSelect;

        document.body.style.userSelect = 'none';
        document.body.style.cursor = IS_VERTICAL ? 'row-resize' : 'col-resize';

        return () => {
            document.body.style.userSelect = PREV;
            document.body.style.cursor = '';
        };

    }, [useIsDragging, IS_VERTICAL]);

    const [FIRST, SECOND] = Array.isArray(children) ? children : [children, null];

    const SIZE = useCollapsed ? 0 : usePercent;

    return (
        <div
            ref={REF__useRefRoot}
            className={BLOCK}
            data-orientation={orientation}
            data-dragging={useIsDragging}
            style={{ '--LS__split_size': `${SIZE}%` }}
        >

            <div className={`${BLOCK}__pane`} data-pane="first">{FIRST}</div>

            <div
                className={`${BLOCK}__divider`}
                role="separator"
                tabIndex={0}
                aria-orientation={IS_VERTICAL ? 'horizontal' : 'vertical'}
                aria-valuenow={Math.round(SIZE)}
                aria-valuemin={min}
                aria-valuemax={max}
                aria-label="Redimensionar paneles"
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onKeyDown={onKeyDown}
                onDoubleClick={() => isCollapsible && setCollapsed(!useCollapsed)}
            >
                <span className={`${BLOCK}__grip`} aria-hidden="true" />
            </div>

            <div className={`${BLOCK}__pane`} data-pane="second">{SECOND}</div>

        </div>
    );
};
